import { Component, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { ResponsibilitiesService } from "src/app/features/responsibilities/services/responsibilities.service";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { DashboardsService } from "../../services/dashboard.service";
import { ChartData, ChartOptions, ChartType } from "chart.js";
import { Label } from "chartist";

function stripTime(date: Date | null): Date | null {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

enum TipoContratoUsuario {
  Clt = "Clt",
  Autonomo = "Autônomo",
  Fornecedor = "Fornecedor",
}

@Component({
  selector: "app-manager-home",
  templateUrl: "./manager-home.component.html",
  styleUrls: ["./manager-home.component.scss"],
})
export class ManagerHomeComponent implements OnInit {
  columns = ["name", "tipo", "cargo", "metas", "tarefas", "docs", "acesso"];
  cargos = [];

  filtros = {
    start: new Date(new Date().setDate(new Date().getDate() - 30)),
    end: new Date(),
    cargo: "",
    nome: "",
  };

  kpis = [
    { label: "Metas Atribuídas", value: 0, icon: "check_circle" },
    { label: "Metas Concluídas", value: 0, icon: "check_circle" },
    { label: "Tarefas Atribuídas", value: 0, icon: "check_circle" },
    { label: "Tarefas Concluídas", value: 0, icon: "check_circle" },
  ];

  recentActivities = [
    { icon: "person_add", text: "Novo funcionário João Silva foi adicionado", time: "2 horas atrás" },
    { icon: "task_alt", text: "Meta mensal de vendas foi concluída", time: "4 horas atrás" },
    { icon: "description", text: "Documento de políticas foi atualizado", time: "1 dia atrás" },
    { icon: "assignment", text: "3 novas tarefas foram atribuídas", time: "2 dias atrás" },
  ];

  // Performance Analysis Data
  selectedPeriod: string = "week";

  // Evaluations Chart Data
  evaluationsChartData: ChartData<"line"> = {
    labels: [],
    datasets: [],
  };

  evaluationsChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#374151",
        titleColor: "#f9fafb",
        bodyColor: "#f9fafb",
        borderColor: "#6b7280",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: "#e5e7eb",
        },
        ticks: {
          color: "#6b7280",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#e5e7eb",
        },
        ticks: {
          color: "#6b7280",
        },
      },
    },
  };

  // Goals and Tasks Chart Data
  goalsTasksChartData: ChartData<"bar"> = {
    labels: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun"],
    datasets: [
      {
        label: "Metas",
        data: [12, 19, 15, 25, 22, 30],
        backgroundColor: "#4b5563",
        borderColor: "#374151",
        borderWidth: 1,
      },
      {
        label: "Tarefas",
        data: [8, 15, 12, 20, 18, 25],
        backgroundColor: "#6b7280",
        borderColor: "#4b5563",
        borderWidth: 1,
      },
    ],
  };

  goalsTasksChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#374151",
        titleColor: "#f9fafb",
        bodyColor: "#f9fafb",
        borderColor: "#6b7280",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: "#e5e7eb",
        },
        ticks: {
          color: "#6b7280",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#e5e7eb",
        },
        ticks: {
          color: "#6b7280",
        },
      },
    },
  };

  kpisTasks = [];
  colaboradores = [];
  autonomos = [];
  prestadores = [];

  barChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true },
    },
  };
  barChartData: ChartData<"bar"> = {
    labels: ["João", "Maria", "Pedro"],
    datasets: [
      {
        data: [5, 8, 3],
        label: "Metas concluídas",
        backgroundColor: "#42A5F5",
      },
    ],
  };

  // Gráfico de pizza ou doughnut
  doughnutChartType: ChartType = "doughnut";
  doughnutChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
    },
  };
  doughnutChartData: ChartData<"doughnut"> = {
    labels: ["Lido", "Não lido"],
    datasets: [
      {
        data: [0, 100],
        backgroundColor: ["#4CAF50", "#F44336"],
        hoverOffset: 10,
      },
    ],
  };

  constructor(
    private responsibilitiesService: ResponsibilitiesService,
    private toast: ToastrService,
    private errorHandlerService: ErrorHandlerService,
    private dashboardService: DashboardsService,
  ) {}

  ngOnInit(): void {
    this.fetchCargos();
    this.fetchOverview();
    this.initializeCharts();

    this.countAllGoalsCreated();
    this.countAllGoalsFinished();

    this.countAllTasksCreated();
    this.countAllTasksFinished();
  }

  private countAllTasksFinished(): void {
    this.dashboardService.countAllTasksFinished().subscribe({
      next: (response) => {
        this.kpis[3].value = response;
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar as tarefas finalizadas");
      },
    });
  }

  private countAllTasksCreated(): void {
    this.dashboardService.countAllTasksCreated().subscribe({
      next: (response) => {
        this.kpis[2].value = response;
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar as tarefas criadas");
      },
    });
  }

  private countAllGoalsCreated(): void {
    this.dashboardService.countAllGoalsCreated().subscribe({
      next: (response) => {
        this.kpis[0].value = response;
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar as metas criadas");
      },
    });
  }

  private countAllGoalsFinished(): void {
    this.dashboardService.countAllGoalsFinished().subscribe({
      next: (response) => {
        this.kpis[1].value = response;
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar as metas finalizadas");
      },
    });
  }

  private fetchOverview(): void {
    this.dashboardService.getOverview().subscribe({
      next: (response) => {
        const novosColaboradores = [];
        const novosAutonomos = [];
        const novosPrestadores = [];

        response.map((data) => {
          switch (data.contrato) {
            case TipoContratoUsuario.Clt:
              this.buildColaboradores(data, novosColaboradores);
              break;
            case TipoContratoUsuario.Autonomo:
              this.buildAutonomos(data, novosAutonomos);
              break;
            case TipoContratoUsuario.Fornecedor:
              this.buildPrestadores(data, novosPrestadores);
              break;
          }
        });

        this.colaboradores = novosColaboradores;
        this.autonomos = novosAutonomos;
        this.prestadores = novosPrestadores;
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar os cargos");
      },
    });
  }

  private buildColaboradores(data: any, novosColaborades: any[]): void {
    const colaborador = {
      name: data.name,
      tipo: data.tipo,
      cargo: data.cargo,
      metas: data.metas,
      tarefas: data.tarefas,
      docsLidos: 100,
      lastLogin: new Date(),
    };
    novosColaborades.push(colaborador);
  }

  private buildAutonomos(data: any, novosAutonomos: any[]): void {
    const autonomo = {
      name: data.name,
      tipo: data.tipo,
      cargo: data.cargo,
      metas: data.metas,
      tarefas: data.tarefas,
      docsLidos: 100,
      lastLogin: new Date(),
    };
    novosAutonomos.push(autonomo);
  }

  private buildPrestadores(data: any, novosPrestadores: any[]): void {
    const prestador = {
      name: data.name,
      tipo: data.tipo,
      cargo: data.cargo,
      metas: data.metas,
      tarefas: data.tarefas,
      docsLidos: 100,
      lastLogin: new Date(),
    };
    novosPrestadores.push(prestador);
  }

  private fetchCargos(): void {
    this.responsibilitiesService.findAll().subscribe({
      next: (cargos) => {
        cargos.map((cargo) => {
          this.cargos.push(cargo.name);
        });
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar os cargos");
      },
    });
  }

  private setCleanUpKpisTasks(): void {
    this.kpisTasks = [];
  }

  aplicarFiltros() {
    const startDateOnly = stripTime(this.filtros.start);
    const endDateOnly = stripTime(this.filtros.end);
    const startString = startDateOnly?.toISOString().substring(0, 10);
    const endString = endDateOnly?.toISOString().substring(0, 10);

    this.dashboardService.getOverviewFilter(startString, endString).subscribe({
      next: (response) => {
        this.setCleanUpKpisTasks();
        this.buildKpiTarefasParaFazer(response);
        this.buildKpiEmAndamento(response);
        this.buildKpiTarefasConcluidas(response);
      },
      error: (error) => {
        this.errorHandlerService.handle(error, "Não foi possível buscar os cargos");
      },
    });
  }

  private buildKpiTarefasParaFazer(response: any): void {
    this.kpisTasks.push({
      label: "Tarefas Para Fazer",
      value: response.quantidadeTarefasParaFazer,
      icon: "pending_actions",
    });
  }

  private buildKpiEmAndamento(response: any): void {
    this.kpisTasks.push({
      label: "Tarefas Em Andamento",
      value: response.quantidadeTarefasEmAndamento,
      icon: "trending_up",
    });
  }

  private buildKpiTarefasConcluidas(response: any): void {
    this.kpisTasks.push({
      label: "Tarefas Concluídas",
      value: response.quantidadeTarefasConcluidas,
      icon: "check_circle",
    });
  }

  // Métodos para calcular estatísticas da equipe
  getTotalMetas(team: any[]): number {
    return team.reduce((total, member) => total + (member.metas || 0), 0);
  }

  getTotalTarefas(team: any[]): number {
    return team.reduce((total, member) => total + (member.tarefas || 0), 0);
  }

  getAverageDocs(team: any[]): number {
    if (team.length === 0) return 0;
    const totalDocs = team.reduce((total, member) => total + (member.docsLidos || 0), 0);
    return Math.round(totalDocs / team.length);
  }

  // Performance Analysis Methods
  private initializeCharts(): void {
    this.updateEvaluationsChart();
  }

  selectPeriod(period: string): void {
    this.selectedPeriod = period;
    this.updateEvaluationsChart();
  }

  private updateEvaluationsChart(): void {
    const data = this.getEvaluationsData();
    this.evaluationsChartData = {
      labels: data.labels,
      datasets: [
        {
          label: "Avaliações",
          data: data.values,
          borderColor: "#4b5563",
          backgroundColor: "rgba(75, 85, 99, 0.1)",
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: "#4b5563",
          pointBorderColor: "#374151",
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  }

  private getEvaluationsData(): { labels: string[]; values: number[] } {
    switch (this.selectedPeriod) {
      case "week":
        return {
          labels: ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"],
          values: [5, 8, 12, 15, 10, 7, 9],
        };
      case "month":
        return {
          labels: ["Sem 1", "Sem 2", "Sem 3", "Sem 4"],
          values: [45, 52, 48, 61],
        };
      case "quarter":
        return {
          labels: ["Mês 1", "Mês 2", "Mês 3"],
          values: [180, 195, 220],
        };
      default:
        return { labels: [], values: [] };
    }
  }

  getTotalEvaluations(): number {
    const data = this.getEvaluationsData();
    return data.values.reduce((total, value) => total + value, 0);
  }

  getAverageEvaluations(): number {
    const data = this.getEvaluationsData();
    const total = this.getTotalEvaluations();
    return Math.round(total / data.values.length);
  }

  getTotalGoals(): number {
    return this.goalsTasksChartData.datasets[0].data.reduce((total, value) => total + value, 0);
  }

  getTotalTasks(): number {
    return this.goalsTasksChartData.datasets[1].data.reduce((total, value) => total + value, 0);
  }

  getCompletionRate(): number {
    const totalGoals = this.getTotalGoals();
    const totalTasks = this.getTotalTasks();
    const total = totalGoals + totalTasks;
    return total > 0 ? Math.round((totalTasks / total) * 100) : 0;
  }
}
